import { Injectable } from '@angular/core';
import { RuleId } from '../models/rule-id';
import { RuleListType } from '../models/enums';
import { FilterBlock, Condition, ConditionOperator } from '../models/parsing-models';

/*
 * FilterBlock class and parser for Path of Exile item filter blocks.
 *
 * The Path of Exile item filter syntax defines rules that start with either
 * `Show` or `Hide` followed by a list of conditions and actions.  A
 * `Continue` line (also referred to as `Partial` in this API) tells the
 * filter engine to keep matching subsequent blocks even when this block
 * matches.  Conditions control what items the block applies to, while
 * actions change the appearance or sound of matched items.  This class
 * exposes fields for all documented conditions and actions as of the
 * Path of Exile item filter documentation【783959105439412†L170-L269】【783959105439412†L480-L578】.
 *
 * To use the parser, inject the ParserService and call `parseFilterBlock(text)`
 * with the text of a single filter block (including the leading `Show`/`Hide` line).
 * The returned object will have boolean `show` or `hide` flags set to true
 * depending on the block type, a `partial` flag indicating the
 * presence of `Continue`, and nested `conditions` and `actions`
 * objects containing raw string values from the filter lines.  Unknown
 * directives are stored under `conditions` as they behave like
 * conditions in the filter language.
 */

@Injectable({
  providedIn: 'root'
})
export class RuleParserService {
  private readonly actionNames = new Set([
    'playalertsound',
    'playalertsoundpositional',
    'customalertsound',
    'customalertsoundoptional',
    'disabledropsound',
    'enabledropsound',
    'disabledropsoundifalertsound',
    'enabledropsoundifalertsound',
    'minimapicon',
    'playeffect',
    'setbackgroundcolor',
    'setbordercolor',
    'setfontsize',
    'settextcolor'
  ]);

  /**
   * Parse a single filter block into a {@link FilterBlock} instance.  The
   * block must begin with a `Show` or `Hide` directive.  Lines that do not
   * match any known condition or action are stored under `conditions` by
   * default.  The parser does not evaluate comparison operators or
   * convert numeric values – it preserves the raw value text so callers
   * can handle advanced parsing if needed.
   *
   * @param text The raw text of a filter block, including the first line.
   */
  parseFilterBlock(text: string): FilterBlock {
    const block = new FilterBlock();
    block.fullText = text;

    const lines = text
      .split(/\r?\n/)
      .map(l => l.trim())
      .filter(l => l.length > 0);
    if (lines.length === 0) {
      return block;
    }

    // Determine block type (Show/Hide) and parse metadata from first line.
    const firstLine = lines[0];
    const firstWord = firstLine.split(/\s+/)[0];
    if (firstWord === 'Show') {
      block.show = true;
    } else if (firstWord === 'Hide') {
      block.hide = true;
    } else {
      // Unknown block type; treat as Show for safety.
      block.show = true;
    }

    // Parse name and id from the first line if it contains a comment.
    // Format: Show # Name (id) or Show # Name (id/type/index)
    const commentMatch = firstLine.match(/#\s*(.+?)\s*\(([^)]+)\)/);
    if (commentMatch) {
      block.ruleDescription.name = commentMatch[1].trim();
      const id = commentMatch[2].trim();
      block.ruleDescription.completeId = id;
      block.ruleDescription.id = id as RuleId;

      // Parse ruleListType and ruleListIndex from id if it contains a path structure.
      // Format: something/type/index
      const idParts = id.split('/');
      if (idParts.length >= 2) {
        const ruleListType = idParts[1].trim();
        // Check if it's a valid RuleListType
        const validTypes: RuleListType[] = ['weapon', 'armour', 'jewellery', 'jewel', 'flask', 'custom', 'cosmetic'];
        if (validTypes.includes(ruleListType as RuleListType)) {
          block.ruleDescription.ruleListType = ruleListType as RuleListType;
        }

        // Parse index if present
        if (idParts.length >= 3) {
          const index = parseInt(idParts[2].trim(), 10);
          if (!Number.isNaN(index)) {
            block.ruleDescription.ruleListIndex = index;
          }
        }
      }
    }

    // Process remaining lines.
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      // Skip comment lines (start with #) or empty lines.
      if (!line || line.startsWith('#')) continue;
      const parts = line.split(/\s+/);
      const directive = parts[0];
      // Handle the `Continue` directive, which makes this block partial.
      if (directive.toLowerCase() === 'continue') {
        block.partial = true;
        continue;
      }
      const remainder = line.slice(directive.length).trim();
      const key = this.toCamelCase(directive);
      const lower = directive.toLowerCase();
      if (this.actionNames.has(lower)) {
        // Actions retain their raw value string.
        block.actions[key] = remainder;
      } else {
        // Parse the condition into an operator and value.
        const cond = this.parseCondition(remainder);
        const existing = block.conditions[key];
        if (existing === undefined) {
          block.conditions[key] = cond;
        } else if (Array.isArray(existing)) {
          // Already have multiple conditions recorded for this key.
          // For hasExplicitMod, we need all conditions to match, so add to array.
          if (key === 'hasExplicitMod') {
            existing.push(cond);
          } else {
            // For other conditions, ignore duplicates beyond the first two to avoid ambiguous logic.
            continue;
          }
        } else {
          // existing is a single Condition.
          // For hasExplicitMod, convert to array to allow multiple conditions.
          if (key === 'hasExplicitMod') {
            block.conditions[key] = [existing as Condition, cond];
          } else {
            // Check for possible merge into 'between' for numeric conditions.
            const exCond = existing as Condition;
            const opA = exCond.operator;
            const opB = cond.operator;
            const canMerge =
              ((opA === ConditionOperator.GreaterThan && opB === ConditionOperator.LessThan) ||
                (opA === ConditionOperator.LessThan && opB === ConditionOperator.GreaterThan));
            if (canMerge) {
              // Try to parse numeric values from both conditions.
              const valA = parseFloat(exCond.value);
              const valB = parseFloat(cond.value);
              const isNumA = !Number.isNaN(valA);
              const isNumB = !Number.isNaN(valB);
              if (isNumA && isNumB) {
                const min = Math.min(valA, valB);
                const max = Math.max(valA, valB);
                block.conditions[key] = {
                  operator: ConditionOperator.Between,
                  value: `${min} ${max}`
                };
              }
              // If merging fails due to non‑numeric values, ignore the new condition.
            }
            // If not mergeable, ignore the new condition entirely.
          }
        }
      }
    }
    return block;
  }

  /**
   * Helper to convert a directive name (as it appears in the filter file) to
   * camelCase property name.  For example, `BaseArmour` becomes
   * `baseArmour`.  The conversion is limited to an initial uppercase
   * character and leaves the rest of the name intact.  Unknown names are
   * returned unchanged to avoid mangling acronyms.
   */
  private toCamelCase(name: string): string {
    if (!name) return name;
    return name.charAt(0).toLowerCase() + name.slice(1);
  }

  /**
   * Parse the remainder of a condition line into its operator and value.
   * Recognised comparison operators are extracted from the start of the
   * string.  If no operator is found and the remainder consists of two
   * whitespace‑separated tokens that both look like numbers, the
   * operator `between` is used and the two tokens are concatenated with
   * a single space.  Otherwise the operator defaults to `==` and the
   * entire remainder becomes the value.  Caller should trim the
   * remainder before passing it into this function.
   */
  private parseCondition(remainder: string): Condition {
    // Remove leading whitespace just in case.
    const r = remainder.trim();
    if (r.length === 0) {
      return { operator: ConditionOperator.Equals, value: '' };
    }
    // Check for explicit operator at the start of the remainder.
    // Whitespace after operator is optional to handle formats like ">=1" and ">= 1"
    const opMatch = r.match(/^(<=|>=|!=|==|=|<|>|!)\s*(.*)$/);
    if (opMatch) {
      const operator = opMatch[1];
      const value = opMatch[2].trim();
      // Map textual operators to enum values.  We ignore strict >/< and treat
      // them as greaterThan/lessThan since the filter language rarely uses
      // exclusive comparisons.【783959105439412†L170-L269】
      let opEnum: ConditionOperator;
      if (operator === '>=' || operator === '>') {
        opEnum = ConditionOperator.GreaterThan;
      } else if (operator === '<=' || operator === '<') {
        opEnum = ConditionOperator.LessThan;
      } else if (operator === '==') {
        opEnum = ConditionOperator.Equals;
      } else if (operator === '!=') {
        // Not equal is treated as equals for consistency; user can check value.
        opEnum = ConditionOperator.Equals;
      } else if (operator === '=') {
        opEnum = ConditionOperator.Equals;
      } else if (operator === '!') {
        // In item filters, '!' acts as not-equal; treat same as Equals.
        opEnum = ConditionOperator.Equals;
      } else {
        opEnum = ConditionOperator.Equals;
      }
      return { operator: opEnum, value };
    }
    // Split on whitespace to see if we have two separate tokens and both look numeric.
    const tokens = r.split(/\s+/);
    if (tokens.length === 2) {
      const [a, b] = tokens;
      const numA = /^[-+]?[0-9]*\.?[0-9]+$/.test(a);
      const numB = /^[-+]?[0-9]*\.?[0-9]+$/.test(b);
      if (numA && numB) {
        return { operator: ConditionOperator.Between, value: `${a} ${b}` };
      }
    }
    // Check if the value is a double-quoted string (for loose string matching).
    const quotedMatch = r.match(/^"([^"]*)"$/);
    if (quotedMatch) {
      return { operator: ConditionOperator.Like, value: quotedMatch[1] };
    }
    // Check if the value is a single word (no spaces) - use loose string matching.
    if (tokens.length === 1) {
      return { operator: ConditionOperator.Like, value: r };
    }
    // Default: equality operator with the entire remainder as value.
    return { operator: ConditionOperator.Equals, value: r };
  }
}

// Example usage (for testing/debugging):
// const text = `Show\n Rarity >= Rare\n ItemLevel > 80\n Sockets 6\n PlayEffect Blue`;\n
